import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PageHeader, EmptyState } from "@/components/Layout";
import { ClientPanel, type ClientFormValue } from "@/components/forms";
import { DataTable, type DataTableColumn } from "@/components/table";
import { Button, ErrorNote, KeyFigures, MoreMenu, useCanWrite } from "@/components/ui";
import { DeleteForever } from "@/components/delete";
import { api, errorMessage } from "@/lib/api";
import { date } from "@/lib/format";
import { useClients, useConnectedCount, useRefreshDirectory } from "@/lib/queries";
import type { ClientListItem } from "@/lib/types";

/*
 * L'elenco dei clienti. L'unità di lavoro è la coppia prop+broker: una pagina
 * che elenca i conti singoli (/conti, tolta) non serve più — quello che
 * mostrava di più (saldi, coppie aperte, operazioni per ciclo) è già nelle
 * tabelle Cicli operativi e Fasi, non si ricrea qui.
 */

/**
 * Quanti conti sono connessi in questo momento, su tutto il tenant.
 *
 * Un conto connesso resta connesso finche' qualcuno non lo stacca, e il
 * gateway si paga sui conti connessi in contemporanea: questo numero e' un
 * costo che scorre, non una statistica.
 */
function ConnessiFigures() {
  const q = useConnectedCount();
  const c = q.data;
  if (!c) return null;
  return (
    <div className="px-6 pt-4">
      <KeyFigures
        items={[
          {
            label: "Conti connessi adesso",
            value: c.connected,
            hint: "Restano connessi finché non li stacchi: il gateway si paga sui conti connessi in contemporanea.",
          },
          { label: "Prop / Broker", value: `${c.byRole.PROP} / ${c.byRole.BROKER}` },
          { label: "Con una gamba a mercato", value: c.withOpenPairs },
          { label: "Sempre accesi", value: c.alwaysOn },
        ]}
      />
    </div>
  );
}

type Filtro = "CICLI_ATTIVI" | "SENZA_CONTI" | "CONTI_CONNESSI";

export function Clients() {
  const canWrite = useCanWrite();
  const navigate = useNavigate();
  const clients = useClients();
  const [panel, setPanel] = useState<{ open: boolean; client: ClientFormValue | null }>({ open: false, client: null });
  const [filtri, setFiltri] = useState<Set<Filtro>>(new Set());
  const [query, setQuery] = useState("");

  const openNew = () => setPanel({ open: true, client: null });
  const toggleFiltro = (f: Filtro) =>
    setFiltri((prev) => {
      const next = new Set(prev);
      next.has(f) ? next.delete(f) : next.add(f);
      return next;
    });

  const q = query.trim().toLowerCase();
  const list = (clients.data ?? []).filter((c) => {
    if (filtri.has("CICLI_ATTIVI") && c.cycles.length === 0) return false;
    if (filtri.has("SENZA_CONTI") && c.accounts.length > 0) return false;
    if (filtri.has("CONTI_CONNESSI") && !c.accounts.some((a) => a.isConnected)) return false;
    if (q && !`${c.name} ${c.email}`.toLowerCase().includes(q)) return false;
    return true;
  });

  const pill = (active: boolean) =>
    `inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs ${
      active ? "border-accent text-ink" : "border-line text-muted hover:text-ink"
    }`;

  return (
    <>
      <PageHeader
        title="Clienti"
        subtitle="Anagrafica, conti, e il proxy e il CID con cui escono"
        actions={
          <>
            <Link to="/clienti/archiviati">
              <Button>Archiviati</Button>
            </Link>
            {canWrite && <Button variant="primary" onClick={openNew}>Nuovo cliente</Button>}
          </>
        }
      />

      <ConnessiFigures />

      {clients.isLoading ? (
        <p className="px-6 py-6 text-sm text-muted">Caricamento…</p>
      ) : clients.error ? (
        <div className="px-6 py-6">
          <ErrorNote>{errorMessage(clients.error)}</ErrorNote>
        </div>
      ) : !clients.data?.length ? (
        <EmptyState
          title="Nessun cliente"
          hint="Crea il primo cliente col suo proxy e il suo CID, poi aggiungi i suoi due conti MT5: uno prop e uno broker. Proxy e CID stanno sul cliente, e valgono per tutti i suoi conti."
          action={canWrite && <Button variant="primary" onClick={openNew}>Nuovo cliente</Button>}
        />
      ) : (
        <div className="space-y-3 px-6 py-4">
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={() => toggleFiltro("CICLI_ATTIVI")} className={pill(filtri.has("CICLI_ATTIVI"))}>
              Con cicli attivi
            </button>
            <button onClick={() => toggleFiltro("SENZA_CONTI")} className={pill(filtri.has("SENZA_CONTI"))}>
              Senza conti
            </button>
            <button onClick={() => toggleFiltro("CONTI_CONNESSI")} className={pill(filtri.has("CONTI_CONNESSI"))}>
              Con conti connessi
            </button>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Cerca nome o email"
              aria-label="Cerca nome o email"
              className="ml-auto w-56 rounded border border-line bg-raised px-3 py-1.5 text-sm text-ink outline-none placeholder:text-faint focus:border-muted"
            />
          </div>
          <ClientsTable
            clients={list}
            canWrite={canWrite}
            onOpen={(id) => navigate(`/clienti/${id}`)}
            onEdit={(c) => setPanel({ open: true, client: c })}
          />
        </div>
      )}

      <ClientPanel
        open={panel.open}
        client={panel.client}
        onClose={() => setPanel({ open: false, client: null })}
        onSaved={(id) => !panel.client && navigate(`/clienti/${id}`)}
      />
    </>
  );
}

function ClientsTable({
  clients,
  canWrite,
  onOpen,
  onEdit,
}: {
  clients: ClientListItem[];
  canWrite: boolean;
  onOpen: (id: string) => void;
  onEdit: (c: ClientListItem) => void;
}) {
  const columns: DataTableColumn<ClientListItem>[] = [
    {
      key: "name",
      header: "Nome",
      cell: (c) => (
        <Link to={`/clienti/${c.id}`} className="font-medium text-ink hover:underline">
          {c.name}
        </Link>
      ),
      sortBy: (c) => c.name.toLowerCase(),
    },
    {
      key: "email",
      header: "Email",
      // Accorciata con i puntini; intera al passaggio del mouse.
      cell: (c) => (
        <span className="block max-w-[14rem] truncate text-muted" title={c.email}>
          {c.email}
        </span>
      ),
      sortBy: (c) => c.email.toLowerCase(),
    },
    {
      key: "propAccounts",
      header: "Conti prop",
      align: "right",
      cell: (c) => c.accounts.filter((a) => a.role === "PROP").length,
      sortBy: (c) => c.accounts.filter((a) => a.role === "PROP").length,
    },
    {
      key: "cycles",
      header: "Cicli attivi",
      align: "right",
      cell: (c) => c.cycles.length,
      sortBy: (c) => c.cycles.length,
    },
    { key: "proxy", header: "Proxy", cell: (c) => <ProxyCell client={c} /> },
    {
      key: "connection",
      header: "Connessione",
      cell: (c) => <ConnessioneCell client={c} />,
      sortBy: (c) => c.accounts.filter((a) => a.isConnected).length,
    },
    {
      key: "actions",
      header: "",
      align: "right",
      cell: (c) => <RowActions client={c} canWrite={canWrite} onOpen={() => onOpen(c.id)} onEdit={() => onEdit(c)} />,
    },
  ];

  return (
    <div className="card">
      <DataTable
        columns={columns}
        rows={clients}
        rowKey={(c) => c.id}
        initialSort={{ key: "name", dir: "asc" }}
        empty={{ title: "Nessun cliente con questi filtri", hint: "Cambia i filtri sopra, o cerca un altro nome." }}
      />
    </div>
  );
}

/** Da dove esce: paese e città si leggono senza svelare la password, perché non ci stanno più dentro. */
function ProxyCell({ client }: { client: ClientListItem }) {
  if (!client.defaultProxy) {
    return (
      <span className="text-warn" title="Senza proxy il conto prop non si connette">
        Nessun proxy
      </span>
    );
  }
  const p = client.defaultProxy;
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        className={`h-2 w-2 rounded-full ${p.lastCheckOk === null ? "bg-faint" : p.lastCheckOk ? "bg-gain" : "bg-loss"}`}
        title={p.lastCheckAt ? `Ultimo controllo ${date(p.lastCheckAt)}` : "Mai controllato"}
        aria-hidden
      />
      <span className="text-ink">{[p.city, p.country].filter(Boolean).join(", ") || `${p.host}:${p.port}`}</span>
    </span>
  );
}

function ConnessioneCell({ client }: { client: ClientListItem }) {
  const total = client.accounts.length;
  if (total === 0) return <span className="text-faint">—</span>;
  const connected = client.accounts.filter((a) => a.isConnected).length;
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`h-2 w-2 rounded-full ${connected === 0 ? "bg-faint" : connected === total ? "bg-gain" : "bg-warn"}`} aria-hidden />
      <span className={connected ? "text-ink" : "text-muted"}>
        {connected}/{total}
      </span>
    </span>
  );
}

function RowActions({
  client,
  canWrite,
  onOpen,
  onEdit,
}: {
  client: ClientListItem;
  canWrite: boolean;
  onOpen: () => void;
  onEdit: () => void;
}) {
  const refresh = useRefreshDirectory();
  const [busy, setBusy] = useState<string | null>(null);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const connectable = client.accounts.filter((a) => a.role !== "NONE");
  const connected = client.accounts.filter((a) => a.isConnected).length;

  /** In sequenza, non in parallelo: Mirror apre una sessione MT5 per conto. */
  const bulk = async (kind: "connect" | "disconnect") => {
    setBusy(kind);
    setMessage(null);
    const targets = connectable.filter((a) => (kind === "connect" ? !a.isConnected : a.isConnected));
    const failures: string[] = [];
    for (const a of targets) {
      try {
        await (kind === "connect" ? api.accounts.connect(a.id) : api.accounts.disconnect(a.id));
      } catch (e) {
        failures.push(`${a.label}: ${errorMessage(e)}`);
      }
    }
    await refresh();
    setBusy(null);
    if (!targets.length) setMessage(kind === "connect" ? "Tutti i conti con un ruolo sono già connessi." : "Nessun conto connesso.");
    else if (failures.length) setMessage(failures.join(" · "));
  };

  /*
   * L'AVVISO PRIMA DI ARCHIVIARE (12/10): i conti attivi del cliente, con
   * numero, server e ruolo. Archiviare il cliente li nasconde dalle liste ma
   * tiene occupati i loro numeri MT5: la scelta e' fra eliminarli (logicamente,
   * si tornano dagli Eliminati) e lasciarli come sono.
   */
  const [archivePlan, setArchivePlan] = useState<Awaited<ReturnType<typeof api.clients.archivePreview>>["accounts"] | null>(null);

  const askArchive = async () => {
    setBusy("archive-preview");
    setMessage(null);
    try {
      setArchivePlan((await api.clients.archivePreview(client.id)).accounts);
      setConfirmArchive(true);
    } catch (e) {
      setMessage(errorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  const archive = async (deleteAccounts: boolean) => {
    setBusy("archive");
    setMessage(null);
    try {
      await api.clients.archive(client.id, { deleteAccounts });
      await refresh();
    } catch (e) {
      setMessage(errorMessage(e));
      setBusy(null);
      setConfirmArchive(false);
    }
  };

  const bloccati = (archivePlan ?? []).filter((a) => a.blocker);

  return (
    <span className="inline-flex flex-col items-end gap-1">
      <span className="inline-flex items-center gap-1">
        <Button size="sm" variant="ghost" onClick={onOpen}>
          Apri
        </Button>
        {canWrite && (
          <>
            <Button size="sm" variant="ghost" onClick={onEdit}>
              Modifica
            </Button>
            <MoreMenu label={`Altre azioni su ${client.name}`}>
              <div>
                <Button
                  size="sm"
                  variant="ghost"
                  className="w-full justify-start"
                  disabled={!!busy || connectable.length === 0}
                  onClick={() => void bulk("connect")}
                >
                  {busy === "connect" ? "Connessione…" : "Connetti tutti"}
                </Button>
              </div>
              <div>
                <Button
                  size="sm"
                  variant="ghost"
                  className="w-full justify-start"
                  disabled={!!busy || connected === 0}
                  onClick={() => void bulk("disconnect")}
                >
                  {busy === "disconnect" ? "Disconnessione…" : "Disconnetti tutti"}
                </Button>
              </div>
              <div>
                {confirmArchive && archivePlan ? (
                  <span className="flex max-w-xs flex-col gap-2 text-xs">
                    {archivePlan.length > 0 && (
                      <>
                        <span className="text-muted">
                          {client.name} ha {archivePlan.length === 1 ? "1 conto attivo" : `${archivePlan.length} conti attivi`}. Archiviato il cliente, i loro numeri MT5
                          restano occupati e non si vedono più.
                        </span>
                        <ul className="space-y-0.5">
                          {archivePlan.map((a) => (
                            <li key={a.id}>
                              <span className="text-ink">{a.label}</span> · {a.role === "PROP" ? "prop" : a.role === "BROKER" ? "broker" : "senza ruolo"} ·{" "}
                              <span className="font-mono">{a.mt5Login}</span> su {a.serverName}
                            </li>
                          ))}
                        </ul>
                        {bloccati.map((a) => (
                          <span key={a.id} className="text-loss">
                            {a.blocker}
                          </span>
                        ))}
                      </>
                    )}
                    {archivePlan.length > 0 ? (
                      <>
                        <Button size="sm" variant="danger" disabled={!!busy || bloccati.length > 0} onClick={() => void archive(true)}>
                          {busy === "archive" ? "Archiviazione…" : "Archivia ed elimina i conti"}
                        </Button>
                        <Button size="sm" disabled={!!busy} onClick={() => void archive(false)}>
                          Archivia lasciando i conti
                        </Button>
                      </>
                    ) : (
                      <Button size="sm" variant="danger" disabled={!!busy} onClick={() => void archive(false)}>
                        {busy === "archive" ? "Archiviazione…" : "Conferma archiviazione"}
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => setConfirmArchive(false)}>
                      Annulla
                    </Button>
                  </span>
                ) : (
                  <Button size="sm" variant="ghost" className="w-full justify-start" disabled={!!busy} onClick={() => void askArchive()}>
                    {busy === "archive-preview" ? "Controllo i conti…" : "Archivia"}
                  </Button>
                )}
              </div>
              {/*
                * Accanto ad «Archivia», non al suo posto: archiviare
                * conserva i riferimenti, eliminare li toglie di mezzo. La
                * differenza la spiega la frase che compare quando si
                * chiede, non un'etichetta.
                */}
              <div>
                <DeleteForever kind="cliente" id={client.id} label={client.name} size="sm" />
              </div>
            </MoreMenu>
          </>
        )}
      </span>
      {message && <span className="text-xs text-loss">{message}</span>}
    </span>
  );
}
